import { el } from "./dom.ts";
import { t, type TranslationKey } from "./i18n.ts";
import { formatBytes } from "./capability.ts";

/**
 * Live export console: fixed stats header over a scrollable, timestamped log.
 *
 * Timestamps come from the viewer's own clock via toLocaleTimeString(), not
 * from anything server-side — "14:32:07" should mean what the person watching
 * it thinks it means, in their timezone.
 */

export type ConsoleLineKind = "info" | "ok" | "warn" | "err";

export interface ConsoleStats {
    /** Label of the conversation currently downloading. */
    currentLabel: string | null;
    /** Bytes written for the current conversation so far. */
    currentBytes: number;
    /**
     * Bytes downloaded in this invocation only — resets to 0 every time an
     * export starts or resumes. On a run that finds nothing new to fetch (a
     * repeat run over already-complete data) this is correctly 0, which reads
     * as "broken" next to a log line reporting megabytes already saved unless
     * it is labelled as session-scoped rather than as a running total.
     */
    sessionBytes: number;
    /**
     * Bytes actually on disk for this module — restored history plus this
     * session's writes. This is the number that answers "how much do I have
     * so far," and it is the one that should read as a running total.
     */
    allTimeBytes: number;
    itemsDone: number;
    itemsTotal: number;
}

export interface ExportConsole {
    readonly root: HTMLElement;
    setStats(stats: Partial<ConsoleStats>): void;
    log(message: string, kind?: ConsoleLineKind): void;
    clear(): void;
    reset(): void;
}

/** Lines beyond this are dropped from the DOM oldest-first. */
const MAX_LINES = 500;

function statRow(labelKey: TranslationKey, valueEl: HTMLElement): HTMLElement {
    return el("div", { class: "console-stat" }, [
        el("span", { class: "console-stat-label", "data-i18n": labelKey }, [t(labelKey)]),
        valueEl
    ]);
}

export function createExportConsole(): ExportConsole {
    const currentValue = el("span", { class: "console-stat-value" }, ["—"]);
    const sessionValue = el("span", { class: "console-stat-value" }, [formatBytes(0)]);
    const allTimeValue = el("span", { class: "console-stat-value" }, [formatBytes(0)]);
    const progressValue = el("span", { class: "console-stat-value" }, ["—"]);

    const progressBar = el("div", { class: "progress-bar-fill" });
    const progressTrack = el("div", { class: "progress-bar" }, [progressBar]);

    const header = el("div", { class: "console-header" }, [
        statRow("console.current", currentValue),
        statRow("console.total", allTimeValue),
        statRow("console.session", sessionValue),
        statRow("console.progress", progressValue),
        progressTrack
    ]);

    const lines = el("div", { class: "console-lines", role: "log", "aria-live": "polite" });
    const root = el("div", { class: "console" }, [header, lines]);

    const stats: ConsoleStats = {
        currentLabel: null,
        currentBytes: 0,
        sessionBytes: 0,
        allTimeBytes: 0,
        itemsDone: 0,
        itemsTotal: 0
    };

    const renderStats = () => {
        currentValue.textContent = stats.currentLabel
            ? `${stats.currentLabel} (${formatBytes(stats.currentBytes)})`
            : "—";

        allTimeValue.textContent = formatBytes(stats.allTimeBytes);
        sessionValue.textContent = formatBytes(stats.sessionBytes);

        progressValue.textContent =
            stats.itemsTotal > 0
                ? t("console.progressValue", {
                      done: stats.itemsDone,
                      total: stats.itemsTotal
                  })
                : "—";

        const pct =
            stats.itemsTotal > 0 ? Math.round((stats.itemsDone / stats.itemsTotal) * 100) : 0;
        progressBar.style.width = `${pct}%`;
    };

    renderStats();

    return {
        root,

        setStats(patch) {
            Object.assign(stats, patch);
            renderStats();
        },

        log(message, kind = "info") {
            // Only autoscroll when already pinned to the bottom, so scrolling
            // back to read an earlier line isn't yanked away by the next write.
            const pinned =
                lines.scrollHeight - lines.scrollTop - lines.clientHeight < 24;

            const time = new Date().toLocaleTimeString();
            lines.append(
                el("div", { class: `console-line ${kind}` }, [
                    el("span", { class: "console-time" }, [time]),
                    el("span", { class: "console-msg" }, [message])
                ])
            );

            while (lines.childElementCount > MAX_LINES) {
                lines.firstElementChild?.remove();
            }

            if (pinned) {
                lines.scrollTop = lines.scrollHeight;
            }
        },

        clear() {
            lines.replaceChildren();
        },

        reset() {
            Object.assign(stats, {
                currentLabel: null,
                currentBytes: 0,
                sessionBytes: 0,
                allTimeBytes: 0,
                itemsDone: 0,
                itemsTotal: 0
            });
            renderStats();
            lines.replaceChildren();
        }
    };
}
